import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import CTASection from "@/components/CTASection";
import BeholdFeed from "@/components/BeholdFeed";

const LOGOS = [
  { src: "/logos/harcourts.png", alt: "Harcourts" },
  { src: "/logos/barfoot-thompson.png", alt: "Barfoot & Thompson" },
  { src: "/logos/hiyoga.png", alt: "Hiyoga" },
  { src: "/logos/mortgage-lab.png", alt: "Mortgage Lab" },
  { src: "/logos/flex-fitness.png", alt: "Flex Fitness" },
  { src: "/logos/af.webp", alt: "Anytime Fitness" },
  { src: "/logos/cobalt.png", alt: "Cobalt" },
  { src: "/logos/stihl.svg", alt: "Stihl" },
  { src: "/logos/brand-01.webp", alt: "Brand" },
  { src: "/logos/brand-03.jpg", alt: "Brand" },
  { src: "/logos/brand-04.png", alt: "Brand" },
];

const IG_TILES = [
  "/images/stock-02.jpg",
  "/images/broker-01.jpg",
  "/images/fitness-02.jpg",
  "/images/prof-02.jpg",
  "/images/stock-04.jpg",
  "/images/trades-01.jpg",
];

const IG_URL = "https://www.instagram.com/onboardmarketing.nz/";
const IG_HANDLE = "@onboardmarketing.nz";

export default function Home() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [igPosts, setIgPosts] = useState(null);

  const HOME_CASE_SLUGS = [
    "better-creative-better-results",
    "cutting-lead-costs",
    "scaling-appraisal-campaigns",
    "caltex-google-ads",
  ];

  useEffect(() => {
    api
      .get("/case-studies")
      .then((r) => {
        const bySlug = Object.fromEntries(r.data.map((c) => [c.slug, c]));
        setCaseStudies(HOME_CASE_SLUGS.map((s) => bySlug[s]).filter(Boolean));
      })
      .catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api
      .get("/instagram-feed")
      .then((r) => {
        if (Array.isArray(r.data?.posts) && r.data.posts.length > 0) {
          setIgPosts(r.data.posts.slice(0, 6));
        }
      })
      .catch(() => {});
  }, []);

  const tiles = igPosts
    ? igPosts.map((p) => ({ src: p.image, href: p.permalink || IG_URL }))
    : IG_TILES.map((src) => ({ src, href: IG_URL }));

  return (
    <div data-testid="home-page">
      <SEO />
      {/* HERO */}
      <section className="pt-10 pb-20 lg:pt-16 lg:pb-24" data-testid="hero">
        <div className="ob-container">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-8">
              <h1 className="ob-h1">
                A team that thinks like
                <br />
                part of your business.
              </h1>

              <p className="ob-lead mt-10 max-w-2xl">
                We work alongside businesses to bring together strategy, creativity and thoughtful marketing that helps them grow. Whether we're launching a new brand, creating content or building campaigns, we care about getting the little things right.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" className="ob-btn ob-btn-primary h-12" data-testid="hero-cta-primary">
                  Let's have a chat
                  <ArrowUpRight size={16} strokeWidth={1.5} className="ml-1" />
                </Link>
                <Link to="/case-studies" className="ob-btn h-12" data-testid="hero-cta-secondary">
                  See what we've been working on
                </Link>
              </div>
            </div>

            {/* Small image hidden on mobile */}
            <div className="hidden lg:block lg:col-span-4 lg:pt-4">
              <div className="rounded-3xl overflow-hidden aspect-[4/5]">
                <img src="/images/onboard/home-hero.jpg" alt="Onboard Marketing team" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A FEW PROJECTS WE'RE PROUD OF */}
      <section className="ob-section" style={{ background: "var(--ob-bg-panel)" }} data-testid="proud-projects">
        <div className="ob-container">
          <div className="max-w-3xl mb-16">
            <h2 className="ob-h2">Projects we're proud of.</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-10 lg:gap-12">
            {caseStudies.slice(0, 4).map((cs, idx) => (
              <Link
                key={cs.slug}
                to="/case-studies"
                data-testid={`proud-${cs.slug}`}
                className={`group block ${idx % 2 === 1 ? "md:mt-16" : ""}`}
              >
                <div className="aspect-[4/3] rounded-3xl overflow-hidden mb-6">
                  <img src={cs.image} alt={cs.client} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
                </div>
                <div className="ob-eyebrow mb-2">{cs.industry}</div>
                <h3 className="ob-h3">{cs.heading || cs.client}</h3>
                <p className="ob-body mt-3 max-w-lg">{cs.challenge}</p>
                {cs.results?.[0] && (
                  <div className="mt-6">
                    <div className="text-[36px] font-light leading-none" style={{ color: "var(--ob-ink)" }}>{cs.results[0].metric}</div>
                    <div className="text-[14px] mt-2" style={{ color: "var(--ob-muted)" }}>{cs.results[0].label}</div>
                  </div>
                )}
                <div className="mt-6 inline-flex items-center gap-2 text-[15px] group-hover:opacity-70 transition-opacity" style={{ color: "var(--ob-ink)" }}>
                  Read the story <ArrowUpRight size={16} strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGO WALL - with title */}
      <section className="py-24 lg:py-32" data-testid="client-wall">
        <div className="ob-container">
          <h2 className="ob-h2 max-w-3xl mb-14">Businesses we have the pleasure of working with.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-6 sm:gap-x-10 lg:gap-x-14 gap-y-10 lg:gap-y-12 items-center justify-items-center">
            {LOGOS.map((l, i) => (
              <div key={i} className="h-14 sm:h-16 lg:h-20 w-full flex items-center justify-center px-2">
                <img
                  src={l.src}
                  alt={l.alt}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KIND WORDS */}
      <section className="ob-section" data-testid="kind-words">
        <div className="ob-container">
          <div className="max-w-3xl mb-16">
            <h2 className="ob-h2">Kind words.</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((t) => (
              <figure key={t.id} className="p-8 md:p-10 rounded-3xl border border-[var(--ob-border)]" style={{ background: "var(--ob-bg-alt)" }}>
                <blockquote className="text-[22px] leading-[1.35] font-light" style={{ color: "var(--ob-ink)" }}>
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 text-[14px]" style={{ color: "var(--ob-muted)" }}>
                  <span style={{ color: "var(--ob-ink)" }}>{t.author}</span> · {t.role}, {t.company}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="ob-section" style={{ background: "var(--ob-bg-alt)" }} data-testid="instagram-section">
        <div className="ob-container">
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-14">
            <div className="lg:col-span-8">
              <h2 className="ob-h2">Get to know us better.</h2>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <a
                href={IG_URL}
                target="_blank"
                rel="noreferrer"
                className="ob-btn ob-btn-primary h-12"
                data-testid="ig-follow"
              >
                <Instagram size={16} strokeWidth={1.5} className="mr-1" />
                Follow {IG_HANDLE}
              </a>
            </div>
          </div>

          <div className="mt-2">
            <BeholdFeed />
          </div>
        </div>
      </section>

      {/* FREE RESOURCES — COMING SOON */}
      <section className="ob-section" data-testid="resources-preview">
        <div className="ob-container">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <h2 className="ob-h2">Free resources.</h2>
            </div>
            <div className="lg:col-span-5 self-end">
              <p className="ob-lead">
                Practical guides, templates and short reads to help you sharpen your own marketing. Currently in the works.
              </p>
            </div>
          </div>

          <div
            className="rounded-3xl border border-[var(--ob-border)] p-14 lg:p-20 flex flex-col items-center text-center"
            style={{ background: "var(--ob-bg-alt)" }}
            data-testid="resources-coming-soon"
          >
            <span className="ob-eyebrow mb-6">In the works</span>
            <div
              className="font-light leading-none"
              style={{
                color: "var(--ob-ink)",
                fontSize: "clamp(48px, 7vw, 96px)",
                letterSpacing: "-0.025em",
              }}
            >
              Coming soon.
            </div>
            <p className="ob-body mt-8 max-w-lg">
              We're pulling together the guides, templates and marketing tips we get asked about most. Pop back in a few weeks, or say hi and we'll share things as soon as they're ready.
            </p>
            <div className="mt-10">
              <Link to="/contact" className="ob-btn ob-btn-primary h-12" data-testid="resources-notify-cta">
                Let us know you're keen
                <ArrowUpRight size={16} strokeWidth={1.5} className="ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
